// **画面の状態と、状態から決まる見え方だけを持つ**（DOM も通信も触らない純粋な関数）。
//
// 画面（DOM の組み立て）と配線（接続・音声・制御）を分ける決まりの「あいだ」にあたる部分で、
// **ここを分けておくとブラウザのプラットフォーム層を捨てるときに判断だけ残せる**
// （DESIGN-0006 §3-2・ADR-0017 §7 のリスク 4）。試験はコンテナ内で完結する。
//
// **何を出すかの正本は `client/画面仕様.md`**（§2 入室前・§3 通話中・§5 状態ごとの見え方）。
// 文言は補足を付けない（同 §6-3）。

import type { Participant, RoomInfo } from "./control.ts";

/** 画面の状態（画面仕様 §5 の 6 つ）。 */
export type Phase =
  /** 繋いでいる最中。入室ボタンは押せない。 */
  | { kind: "connecting" }
  /** 繋がった・未入室。チャンネルの一覧と在室者が出る。 */
  | { kind: "ready" }
  /** 通話中。 */
  | { kind: "in-call" }
  /** 張り直し中。退室と「繋ぎ直す」は押せる。 */
  | { kind: "reconnecting"; attempt: number; max: number }
  /** 6 回で諦めた。自動では何もしない。 */
  | { kind: "gave-up" }
  /** 張り直さない切れ方（満員・バージョン違い・無いグループ・この端末に経路が無い）。 */
  | { kind: "stopped" };

/** 出している画面（2026-10-21 のユーザー判断で 4 つ。画面仕様 §1）。 */
export type Screen = "home" | "call" | "settings" | "about";

/** 一覧の 1 行。**識別子は通信用・`label` は画面用**（ADR-0022 §1-1）。 */
export interface ChannelRow {
  /** 入室要求に載せる識別子（`グループ/チャンネル`）。**中身は読まない。** */
  id: string;
  /** 画面に出す名前。覚えていなければ識別子か「（招待されたチャンネル）」。 */
  label: string;
  /** 在室者の名前を並べたもの。サーバが返さないあいだは空。 */
  who: string;
  /** 「2 / 16」。分からない行（畳まれている・履歴）では空。 */
  count: string;
  /** 招待 URL で来たが一覧に無い（畳まれている）行。 */
  invited: boolean;
  /** 前にいた場所（端末の履歴から補った行。板 #220）。 */
  recalled: boolean;
}

/** 一覧を組むのに要るもの。 */
export interface ChannelListInput {
  /** サーバが返したチャンネル（`ListRooms` の応答か、押し出された写し）。 */
  rooms: readonly RoomInfo[];
  /** いま選んでいるグループ。常設の部屋を見ているときは空。 */
  group: string;
  /** 招待 URL で来た行き先（`グループ/チャンネル`）。無ければ空。 */
  invitedRoom?: string;
  /** 前にいた場所（新しい順。`グループ/チャンネル`）。 */
  recent?: readonly string[];
  /** 端末が覚えている表示名（`グループ/チャンネル` → 表示名）。 */
  names?: Readonly<Record<string, string>>;
}

/** 一覧に無い行き先に出す名前。**覚えていないのは招待 URL で初めて来た人だけ。** */
export const UNKNOWN_CHANNEL_LABEL = "（招待されたチャンネル）";

/** グループの接頭辞を外して画面に出す名前にする。 */
function labelOf(id: string, group: string, names: Readonly<Record<string, string>>): string {
  const remembered = names[id];
  if (remembered) return remembered;
  return group && id.startsWith(`${group}/`) ? id.slice(group.length + 1) : id;
}

/**
 * 一覧の行を組む。
 *
 * **並びは「サーバが返した順 → 畳まれている招待先 → 前にいた場所」**。
 * 畳まれたチャンネルは一覧に出てこない（無人 5 分で畳む。ADR-0019 §1-4）ので、
 * **招待 URL の行き先と、端末が覚えている「前にいた場所」を行として補う**
 * （板 #197 の回答・板 #220 のユーザー判断）。補った行に**添え書きはしない**
 * （2026-10-21 のユーザー指示。人数も出せないので空にする）。
 */
export function channelRows(input: ChannelListInput): ChannelRow[] {
  const { rooms, group, invitedRoom = "", recent = [], names = {} } = input;
  const rows: ChannelRow[] = rooms.map((r) => ({
    id: r.id,
    label: r.display_name || labelOf(r.id, group, names),
    who: (r.participant_names ?? []).join("、"),
    count: `${r.participants} / ${r.max_participants}`,
    invited: false,
    recalled: false,
  }));
  const seen = new Set(rows.map((r) => r.id));

  if (invitedRoom && !seen.has(invitedRoom)) {
    rows.push({
      id: invitedRoom,
      label: names[invitedRoom] || UNKNOWN_CHANNEL_LABEL,
      who: "",
      count: "",
      invited: true,
      recalled: false,
    });
    seen.add(invitedRoom);
  }

  // **前にいた場所は別枠にせず、同じ一覧の中に出す**（板 #220）。
  // いま見ているグループのものだけ（グループを選び替えるとそちらの履歴が見える）
  for (const id of recent) {
    if (seen.has(id)) continue;
    if (group && !id.startsWith(`${group}/`)) continue;
    if (!group && id.includes("/")) continue;
    rows.push({
      id,
      label: labelOf(id, group, names),
      who: "",
      count: "",
      invited: false,
      recalled: true,
    });
    seen.add(id);
  }
  return rows;
}

/**
 * 選んでいた行が消えたら選択を外す（**知らせは出さない**。DESIGN-0006 §4-2）。
 *
 * 押し出しで一覧が変わるので、入室の直前に消えることがある。
 */
export function keepSelection(rows: readonly ChannelRow[], selected: string): string {
  return rows.some((r) => r.id === selected) ? selected : "";
}

/** 入室できるか。**繋がって未入室で、行を選んでいるときだけ**（画面仕様 §5）。 */
export function canJoin(phase: Phase, selected: string): boolean {
  return phase.kind === "ready" && selected !== "";
}

/** 「新しく作る」を押せるか。**繋がって未入室のときだけ**（作る要求を送るため）。 */
export function canCreate(phase: Phase): boolean {
  return phase.kind === "ready";
}

/** 退室を押せるか。**張り直し中も押せる**（待っている張り直しを取り消す。画面仕様 §5）。 */
export function canLeave(phase: Phase): boolean {
  return phase.kind === "in-call" || phase.kind === "reconnecting" || phase.kind === "gave-up";
}

/** 「繋ぎ直す」を押せるか。**張り直し中（取り消して即やり直す）と、諦めたあと**。 */
export function canRetry(phase: Phase): boolean {
  return phase.kind === "reconnecting" || phase.kind === "gave-up" || phase.kind === "stopped";
}

/**
 * 接続の状態として出す短い文字（画面仕様 §4「数値は出さない」）。
 *
 * **張り直しの回数だけは出す**（コアの `Plan` が `attempt` を返す。同 §5）。
 */
export function statusText(phase: Phase): string {
  switch (phase.kind) {
    case "connecting":
      return "接続しています…";
    case "ready":
      return "接続済み";
    case "in-call":
      return "通話中";
    case "reconnecting":
      return `繋ぎ直しています（${phase.attempt}/${phase.max}）`;
    case "gave-up":
      return "繋ぎ直せませんでした";
    case "stopped":
      return "接続できません";
  }
}

/** ミュートのボタンに出す文字。**いまの状態を出す**（2026-10-21 のユーザー指定）。 */
export function muteLabel(muted: boolean): string {
  return muted ? "ミュート ON" : "ミュート OFF";
}

/** 通話中の参加者 1 行。 */
export interface PersonRow {
  id: number;
  /** 画面に出す名前。自分の行だけ分かるようにする。 */
  label: string;
  /** いま話している（光らせる）。**ミュート中の自分は光らせない。** */
  speaking: boolean;
  /** ミュートの印を出す（自分 ＋ 相手のミュート状態）。 */
  muted: boolean;
  /** 受信音量のつまみを出す（自分の行には出さない）。 */
  gain: boolean;
}

export interface PersonListInput {
  roster: readonly Participant[];
  /** 自分の参加者 ID。入室応答が来るまでは null。 */
  self: number | null;
  /** いま話している人（サーバの `speaking`。自分も入る）。 */
  speaking?: ReadonlySet<number>;
  /** 自分がミュートしているか。 */
  muted?: boolean;
  /** ミュートしている相手（S1。バージョン 5 で名簿に載る）。 */
  mutedOthers?: ReadonlySet<number>;
  /** マイクが使えないので聞くだけで入っているか。 */
  listenOnly?: boolean;
}

/**
 * 参加者の行を組む。**自分も載せる**（画面仕様 §3）。
 *
 * 自分が話していることは**コアの発話検出でも分かる**ので、サーバの `speaking` が
 * 届かないあいだ（バージョン 5 前）も自分の行だけは光らせられる。
 */
export function personRows(input: PersonListInput): PersonRow[] {
  const { roster, self, speaking, muted = false, mutedOthers, listenOnly = false } = input;
  return roster.map((p) => {
    const isSelf = p.id === self;
    const silent = isSelf && (muted || listenOnly);
    return {
      id: p.id,
      label: isSelf ? `${p.name}（あなた）` : p.name,
      speaking: !silent && (speaking?.has(p.id) ?? false),
      muted: silent || (!isSelf && (mutedOthers?.has(p.id) ?? false)),
      gain: !isSelf,
    };
  });
}
